import * as React from "react";
import styled from "styled-components";
import cn from "classnames";

import { Placeholder } from "../Placeholder";
import { themeValues } from "../theme";

const Cell = styled.div`
    display: flex;
    flex: 1;
`
Cell.displayName = "StyledCell"

const Row = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 1rem 0;
    width: 100%;

    &.border {
        border-bottom: 1px solid ${themeValues.lightBorderColor};

        &:last-child {
            border-bottom: none;
        }
    }
`
Row.displayName = "StyledRow"

// TODO: get colors, font-sizes etc. from mixins
const StyledTable = styled.div`
  margin-top: 48px;
`
StyledTable.displayName = "StyledTable";

const StyledTh = styled(Cell)`
  font-size: ${themeValues.fontSize.small};
  font-weight: 500;
  padding: 16px 8px;
  color: #808080;
  flex: ${(p: {flex: number}) => p.flex || undefined};
`;
StyledTh.displayName = "StyledTh";

const StyledTr = styled(Row)`
  border-bottom: 1px solid #f0f2f4;
  padding: 0;
  
  .loading {
    opacity: 0.5
  }
`;
StyledTr.displayName = "StyledTr";

const StyledTd = styled(Cell)`
  flex-flow: wrap;
  padding: 16px 8px;
  color: #000;
  font-weight: 500;
  font-size: ${themeValues.fontSize.normal};
  flex: ${(p: {flex: number}) => p.flex || undefined};

  &.ellipsis {
    min-width: 0;
  }

  &.active {
    color: #0446ca;
    font-weight: bold;
  }
`;
StyledTd.displayName = "StyledTd";

// TODO: this should not be exported - find better solution how to share ellilipsis for tables
export const StyledEllipsis = styled.span`
  margin-right: 20px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
`;
StyledEllipsis.displayName = "StyledEllipsis";

// TODO: this should not be exported - find better solution how to share styled button
export const TableStyledButton = styled.button`
  padding: 8px 14px;
  background-color: transparent;
  border: 1px solid #e4e5e8;
  border-radius: 4px;
  transition: all 0.2s ease;
  text-transform: uppercase;
  color: #e4e5e8;
  font-size: 10px;
  font-weight: 500;
  display: block;
  margin: 0 auto;
  outline: 0;

  &:not(:disabled) {
    color: #000;
    cursor: pointer;
  }

  &:hover:not(:disabled) {
    border-color: #000;
  }
`;
TableStyledButton.displayName = "TableStyledButton";

export interface ITableHeaderProps {
  label: string;
  flex?: number;
}

interface ITableProps {
  children: React.ReactNode;
  emptyFallback: React.ReactNode;
  placeholder: boolean | number;
  header: string[] | ITableHeaderProps[];
}

// TODO: data in props instead children
export function Table({ children, emptyFallback, placeholder, header }: ITableProps): JSX.Element {
  function renderRows() {
    if (children) {
      return <div>
        {children}
      </div>;
    }

    // TODO: data props
    return null;
  }

  const headers = header.map((head: string | ITableHeaderProps) => {
    const { flex = 0, label } = typeof head == "string" ? { label: head } : head || {};

    return (
      <StyledTh
        flex={flex}
        key={label}
      >
        {label}
      </StyledTh>
    );
  });

  // TODO: better placeholders - more configurable
  const placeholders = header.map((head: string | ITableHeaderProps) => {
    const { flex = 0, label } = typeof head == "string" ? { label: head } : head || {};

    return (
      <StyledTh
        flex={flex}
        key={label}
      >
        <Placeholder>{label}</Placeholder>
      </StyledTh>
    );
  });

  let placeholderRows = null

  if (placeholder) {
    const placeholderRow = <Tr>{placeholders}</Tr>

    if (typeof placeholder == "number") {
      const rows = []
      for (let i = 0; i < placeholder; i++) {
        rows.push(placeholderRow)
      }
      placeholderRows = <>{rows}</>
    } else {
      placeholderRows = placeholderRow
    }
  }

  return (
    <StyledTable>
      {!emptyFallback ? <Tr>{headers}</Tr> : null}
      {placeholderRows ? placeholderRows : emptyFallback || renderRows()}
    </StyledTable>
  );
}

interface ITableTrProps {
  children: React.ReactNode;
}

export function Tr({ children }: ITableTrProps): JSX.Element {
  return <StyledTr>
    {children}
  </StyledTr>;
}

interface ITableTdProps {
  children: React.ReactNode;
  ellipsis?: boolean;
  flex?: number;
  active?: boolean;
  title?: string;
}

export function Td({ children, ellipsis, flex = 0, active }: ITableTdProps): JSX.Element {
  return <StyledTd flex={flex} className={cn({
    "ellipsis": ellipsis,
    "active": active
  })}>
    {ellipsis ? <StyledEllipsis>{children}</StyledEllipsis> : children}
  </StyledTd>;
}
